import type { Evidence } from "@botallion/contracts";
import { verdictCostLabel } from "@botallion/metering/hooks/dashboard";
import { nameOf } from "../describe.ts";
import { html, link, type Html } from "../html.ts";
import { chip, clock, costChip, empty, shortSha, type ViewContext } from "./common.ts";

function evidenceView(e: Evidence): Html {
  const ref = e.kind === "url" ? link(e.ref) : html`<code>${e.ref}${e.line !== undefined ? `:${e.line}` : ""}</code>`;
  return html`<li><span class="kind">${e.kind}</span> ${ref}${e.detail ? html` <span class="muted">${e.detail}</span>` : ""}</li>`;
}

/** View 5: verdicts per package, per criterion. Latest first, with earlier attempts summarised. */
export function verdictsView(ctx: ViewContext): Html {
  const { state } = ctx;
  if (!state.verdicts.length) return empty("No verdicts yet. The verifier checks each package when an agent asks to land it.");
  const byWp = new Map<string, typeof state.verdicts>();
  for (const v of state.verdicts) byWp.set(v.wpId, [...(byWp.get(v.wpId) ?? []), v]);
  const latestFirst = [...byWp.entries()].sort((a, b) => (b[1].at(-1)?.createdAt ?? "").localeCompare(a[1].at(-1)?.createdAt ?? ""));
  return html`${latestFirst.map(([wpId, list]) => {
    const v = list.at(-1)!;
    const pkg = state.packages[wpId];
    const earlier = list.slice(0, -1);
    return html`<article class="verdict ${v.pass ? "pass" : "fail"}">
      <header>
        <strong>${wpId}</strong> ${pkg ? pkg.title : ""}
        ${chip(v.pass ? "PASS" : "FAIL", v.pass ? "good" : "block")}
        <span class="muted">commit ${shortSha(v.commit)} at ${clock(v.createdAt)} by ${nameOf(state, v.verifiedBy)}${list.length > 1 ? `, attempt ${list.length}` : ""}</span>
        ${ctx.costs !== undefined ? costChip(verdictCostLabel(ctx.costs, v)) : ""}
      </header>
      <table class="criteria"><tbody>${v.results.map((r) => {
        const criterion = pkg?.acceptanceCriteria.find((c) => c.id === r.criterionId);
        return html`<tr class="${r.met ? "met" : "unmet"}">
          <td class="mark">${r.met ? "✓" : "✗"}</td>
          <td><strong>${r.criterionId}</strong> ${criterion?.text ?? ""}<ul class="evidence">${r.evidence.map(evidenceView)}</ul></td>
        </tr>`;
      })}</tbody></table>
      ${v.regressions.length ? html`<div class="regressions"><strong>Regressions:</strong><ul>${v.regressions.map((r) => html`<li>${r.wpId} ${r.criterionId}: ${r.detail}</li>`)}</ul></div>` : ""}
      ${v.notes ? html`<p class="notes">${v.notes}</p>` : ""}
      ${earlier.length ? html`<p class="muted">Earlier: ${earlier.map((e, i) => html`${i ? "; " : ""}${shortSha(e.commit)} ${e.pass ? "passed" : `failed (${e.results.filter((r) => !r.met).map((r) => r.criterionId).join(", ")})`}`)}</p>` : ""}
    </article>`;
  })}`;
}
